export const AUTH_UNAUTHORIZED_EVENT = "trw:auth-unauthorized";

export type Auth = "mod" | "public" | "none";

export type Clock = {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
};

export type ApiClientOptions = {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  clock?: Clock;
  onUnauthorized?: () => void;
  getToken?: () => Promise<string | null>;
  setToken?: (token: string) => Promise<void>;
};

export type RequestOptions = {
  auth?: Auth;
  method?: string;
  body?: BodyInit | null | string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, "");
}

function getModBase(baseUrl: string): string {
  const n = normalizeBaseUrl(baseUrl);
  return n.endsWith("/mod") ? n : `${n}/mod`;
}

function getPublicBase(baseUrl: string): string {
  const n = normalizeBaseUrl(baseUrl);
  return n.endsWith("/mod") ? n.slice(0, -4) : n;
}

function clearAuthHint() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem("trw-auth-hint");
  } catch {
    // Ignore storage errors.
  }
}

function defaultNotify(onUnauthorized?: () => void) {
  if (onUnauthorized) {
    onUnauthorized();
    return;
  }
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
}

async function extractErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const body = JSON.parse(text) as { error?: string };
    if (typeof body?.error === "string" && body.error.trim() !== "") {
      return body.error;
    }
  } catch {
    // Not JSON — fall through.
  }
  return text || `HTTP ${res.status}`;
}

export type ApiClient = {
  request<T>(path: string, opts?: RequestOptions): Promise<T>;
  getBase(): string;
  getPublicBase(): string;
  configure(opts: { baseUrl: string }): void;
  setTokenHandlers(handlers: { getToken?: () => Promise<string | null>; setToken?: (token: string) => Promise<void> }): void;
};

const TOKEN_STORAGE_KEY = "trw_access_token";

function browserStorageGet(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function browserStorageSet(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // Ignore storage errors (private mode, quota).
  }
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  let baseUrl = opts.baseUrl;
  const fetchImpl: typeof fetch =
    opts.fetchImpl ?? ((globalThis.fetch as unknown as typeof fetch).bind(globalThis) as typeof fetch);
  const clock: Clock =
    opts.clock ?? {
      setTimeout: (fn: () => void, ms: number) => setTimeout(fn, ms),
      clearTimeout: (id: unknown) => clearTimeout(id as number),
    };
  const onUnauthorized = opts.onUnauthorized;
  let memoryToken: string | null = null;
  let getToken = opts.getToken ?? (async () => memoryToken ?? browserStorageGet());
  let setToken =
    opts.setToken ??
    (async (token: string) => {
      memoryToken = token;
      browserStorageSet(token);
    });

  let refreshPromise: Promise<void> | null = null;
  let sessionPromise: Promise<void> | null = null;

  function notifyUnauthorized() {
    defaultNotify(onUnauthorized);
  }

  async function handleUnauthorized(): Promise<void> {
    if (!refreshPromise) {
      refreshPromise = (fetchImpl as typeof fetch)(`${getPublicBase(baseUrl)}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      })
        .then((res) => {
          if (!res.ok) throw new Error("Refresh failed");
        })
        .catch((err: unknown) => {
          notifyUnauthorized();
          throw err;
        })
        .finally(() => {
          clock.setTimeout(() => {
            refreshPromise = null;
          }, 500);
        });
    }
    return refreshPromise;
  }

  async function ensureAnonymousSession(): Promise<void> {
    if (!sessionPromise) {
      sessionPromise = (async () => {
        const response = await (fetchImpl as typeof fetch)(`${getPublicBase(baseUrl)}/auth/session`, {
          method: "POST",
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(`Failed to create session: HTTP ${response.status}`);
        }
        const body = (await response.json().catch(() => null)) as { token?: string } | null;
        if (body?.token) {
          await setToken(body.token);
        }
      })().finally(() => {
        sessionPromise = null;
      });
    }
    return sessionPromise;
  }

  async function doRequest<T>(path: string, reqOpts: RequestOptions & { _retried?: boolean } = {}): Promise<T> {
    const auth: Auth = reqOpts.auth ?? "mod";
    const base =
      auth === "none"
        ? normalizeBaseUrl(baseUrl)
        : auth === "mod"
          ? getModBase(baseUrl)
          : getPublicBase(baseUrl);

    const method = (reqOpts.method ?? "GET").toUpperCase();
    const isSimpleGet = method === "GET" || method === "HEAD";

    const headers: Record<string, string> = {};
    const body = reqOpts.body as unknown;
    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

    if (!isFormData && !isSimpleGet) {
      headers["Content-Type"] = "application/json";
    }
    if (reqOpts.headers) {
      for (const [k, v] of Object.entries(reqOpts.headers)) {
        headers[k] = v;
      }
    }

    if (auth === "public") {
      let token = await getToken();
      if (!token && path !== "/auth/session") {
        await ensureAnonymousSession();
        token = await getToken();
      }
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const fetchOpts: RequestInit = {
      method,
      credentials: "include",
      headers,
      signal: reqOpts.signal,
    };
    if (reqOpts.body !== undefined && reqOpts.body !== null) {
      (fetchOpts as RequestInit & { body: BodyInit }).body = reqOpts.body as BodyInit;
    }

    const url = `${base}${path}`;
    let res: Response;
    try {
      res = await (fetchImpl as typeof fetch)(url, fetchOpts);
    } catch (e) {
      throw e;
    }

    if (res.status === 401 && !reqOpts._retried) {
      clearAuthHint();
      if (auth === "public" && path !== "/auth/session") {
        try {
          await ensureAnonymousSession();
          const newToken = await getToken();
          const retryHeaders: Record<string, string> = { ...headers };
          if (newToken) retryHeaders["Authorization"] = `Bearer ${newToken}`;
          else delete retryHeaders["Authorization"];
          const retryOpts: RequestInit = {
            ...fetchOpts,
            headers: retryHeaders,
          };
          let retryRes = await (fetchImpl as typeof fetch)(url, retryOpts);
          if (!retryRes.ok) {
            throw new Error(await extractErrorMessage(retryRes));
          }
          if (retryRes.status === 204 || retryRes.headers.get("content-length") === "0") {
            return undefined as T;
          }
          return retryRes.json() as Promise<T>;
        } catch (e) {
          throw e;
        }
      } else if (auth === "mod") {
        try {
          await handleUnauthorized();
          return doRequest<T>(path, { ...reqOpts, _retried: true });
        } catch {
          throw new Error("Unauthorized");
        }
      }
    }

    if (!res.ok) {
      throw new Error(await extractErrorMessage(res));
    }

    if (res.status === 204 || res.headers.get("content-length") === "0") {
      return undefined as T;
    }

    return res.json() as Promise<T>;
  }

  return {
    request: doRequest,
    getBase: () => getModBase(baseUrl),
    getPublicBase: () => getPublicBase(baseUrl),
    configure: (o: { baseUrl: string }) => {
      baseUrl = o.baseUrl;
    },
    setTokenHandlers: (handlers: { getToken?: () => Promise<string | null>; setToken?: (token: string) => Promise<void> }) => {
      if (handlers.getToken) getToken = handlers.getToken;
      if (handlers.setToken) setToken = handlers.setToken;
    },
  };
}

const defaultClient = createApiClient({ baseUrl: "http://localhost:8000/api/v1" });

export function configureApi(options: { baseUrl: string }) {
  defaultClient.configure(options);
}

export function getApiBase() {
  return defaultClient.getBase();
}

export function getApiPublicBase() {
  return defaultClient.getPublicBase();
}

export const apiClient = defaultClient;
